// Production env vars (Epic 15 deploy):
//   VITE_DISCORD_CLIENT_ID — Discord application client_id (public)
//   VITE_COLYSEUS_URL — wss:// URL of the Fly.io Colyseus server
//
// (Sentry release tag is build-time injected from CF_PAGES_COMMIT_SHA via
// vite.config.ts, NOT via a VITE_SENTRY_RELEASE env var.)

import type { JSX } from 'react';
import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { MotionConfig } from 'framer-motion';
import { GamePhase } from '@susdle/shared';
import { Lobby, type LobbyHandlers } from './lobby/Lobby.js';
import { usePreferences } from './preferences/usePreferences.js';
import { useReducedMotionAttribute } from './preferences/reducedMotion.js';
import { useAudioDirector } from './audio/useAudioDirector.js';
import { GhostMeeting } from './ghost-meeting/GhostMeeting.js';
import { MetaControls } from './shared/components/MetaControls.js';
import { BootLoader } from './boot/BootLoader.js';
import { BootErrorBoundary } from './boot/BootErrorBoundary.js';
import { PageFrame } from './shared/components/PageFrame.js';
import { ConnectionBanner } from './shared/components/ConnectionBanner.js';
import { ClosedDoorCard } from './shared/components/ClosedDoorCard.js';
import { pickBootMode } from './session/bootMode.js';
import { LobbySession, type BootPhase, type ErrorKind } from './session/LobbySession.js';
import { useLobbySession } from './session/useLobbySession.js';
import { usePhaseRouter } from './session/usePhaseRouter.js';
import { useMeetingSession } from './session/useMeetingSession.js';
import { useCluegivingSession } from './session/useCluegivingSession.js';
import { CluegivingPhase } from './clue-giving/CluegivingPhase.js';
import { useDiscussionSession } from './session/useDiscussionSession.js';
import { DiscussionPhase } from './discussion/DiscussionPhase.js';
import { useRevealSession } from './session/useRevealSession.js';
import { RoleReveal } from './discussion/RoleReveal.js';
import { GhostGuessing } from './discussion/GhostGuessing.js';
import { GuessReveal } from './discussion/GuessReveal.js';
import { useGameEndSession } from './session/useGameEndSession.js';
import { GameEnd } from './game-end/GameEnd.js';
import { GameEndResetFade } from './game-end/GameEndResetFade.js';
import { ClockOffsetContext } from './shared/clock.js';
import { DiscordIdentityProvider } from './identity/DiscordIdentityProvider.js';
import { RealDiscordSdkAdapter } from './identity/realDiscordSdkAdapter.js';
import { applyDiscordProxyMappings } from './identity/discordProxyMappings.js';
import { SpeakingProvider } from './voice/SpeakingContext.js';
import { useDiscordSpeaking, type SpeakingEventSource } from './voice/useDiscordSpeaking.js';
import { isSpeakingAllowed } from './voice/speakingGate.js';
import { RealColyseusClient } from './session/colyseusClientPort.js';
import { SessionStorageReconnectStore } from './session/ReconnectTokenStore.js';
import { classifyJoinError } from './session/classifyJoinError.js';
import { classifyIdentityError } from './session/classifyIdentityError.js';
import { closedDoorReason } from './session/bootErrorCopy.js';
import './App.css';

// ---------------------------------------------------------------------------
// Dev-only dynamic imports — gated on import.meta.env.DEV so Vite drops them
// entirely from the production bundle. When DEV is false at build time the
// ternaries collapse to `null` and the import() factories are unreachable.
// ---------------------------------------------------------------------------

// React.lazy factory for MockApp (dev-mock boot path).
const LazyMockApp = import.meta.env.DEV
  ? lazy(() => import('./MockApp.js').then((m) => ({ default: m.MockApp })))
  : null;

// Dynamic-import factory for the dev-real session constructor (dev-real boot path).
const createDevRealSessionFactory: (() => Promise<typeof import('./devRealSession.js')>) | null =
  import.meta.env.DEV ? () => import('./devRealSession.js') : null;

// ---------------------------------------------------------------------------
// Boot-mode picker — evaluated once at module load, never re-runs.
// ---------------------------------------------------------------------------

const mode = pickBootMode(
  { DEV: import.meta.env.DEV, MODE: import.meta.env.MODE },
  new URL(window.location.href),
);

// ---------------------------------------------------------------------------
// Top-level App: branches on boot mode.
// ---------------------------------------------------------------------------

export function App(): JSX.Element {
  if (mode === 'dev-mock' && LazyMockApp !== null) {
    return (
      <Suspense fallback={null}>
        <LazyMockApp />
      </Suspense>
    );
  }
  /* The boundary catches synchronous boot-render throws — chiefly the
     DiscordSDK constructor inside buildBootContext's lazy initializer, which
     throws when the prod URL is opened outside a Discord Activity iframe —
     and renders the closed-door surface instead of a blank page. */
  return (
    <BootErrorBoundary>
      <RealApp mode={mode as 'prod-discord' | 'dev-real'} />
    </BootErrorBoundary>
  );
}

// ---------------------------------------------------------------------------
// RealApp — wires LobbySession → useLobbySession → Lobby.
// ---------------------------------------------------------------------------

interface RealAppProps {
  mode: 'prod-discord' | 'dev-real';
}

interface BootContext {
  session: LobbySession;
  /** Opens an external URL. prod-discord routes through the Activity SDK;
      dev-real falls back to window.open (no sandbox outside Discord). */
  openExternalLink: (url: string) => void;
  /** Voice-activity source for the voice float. prod-discord: the SDK
      adapter (SPEAKING events ride the same RPC socket as identity);
      dev-real: null — no Discord, the provider never mounts, the float
      stays inert exactly as before this wiring. */
  speaking: SpeakingEventSource | null;
}

const openViaWindow = (url: string): void => {
  window.open(url, '_blank', 'noopener,noreferrer');
};

/** Stable empty set handed to the provider while the gate suppresses the
 *  float — referentially constant so the provider's memo doesn't churn. */
const NO_SPEAKERS: ReadonlySet<string> = new Set();

// Constructs the BootContext for `bootMode`. For prod-discord, resolves
// synchronously; for dev-real, awaits the dynamic-import factory.
//
// React StrictMode WILL invoke this twice in dev (lazy-init impurity check).
// That is intentional and SAFE here only because:
//   - Colyseus Client construction does not open a WebSocket (only joinOrCreate does),
//   - the discarded LobbySession is GC'd before its .init() is called,
//   - the SURVIVING session's init() is idempotent (caches inFlightInit).
// If either constructor ever gains a connection side effect, this must be revisited.
function buildBootContext(bootMode: 'prod-discord' | 'dev-real'): Promise<BootContext> {
  if (bootMode === 'prod-discord') {
    // Wire Discord proxy BEFORE constructing the SDK or Colyseus client —
    // patchUrlMappings monkey-patches the global WebSocket/fetch so the
    // SDK's first request (Discord RPC over WS) and the eventual Colyseus
    // connection both route through <client_id>.discordsays.com/.proxy.
    // StrictMode note: `prod-discord` is only reached when `!env.DEV`
    // (see bootMode.ts), and StrictMode is a dev-only construct — this branch
    // never runs twice in a single session.
    const colyseusUrl = import.meta.env.VITE_COLYSEUS_URL as string;
    applyDiscordProxyMappings(colyseusUrl);
    // Hold a reference to the adapter so we can read `instanceId` (a sync
    // SDK-construction-time field) before wrapping in DiscordIdentityProvider.
    const sdkAdapter = new RealDiscordSdkAdapter(import.meta.env.VITE_DISCORD_CLIENT_ID as string);
    return Promise.resolve({
      session: new LobbySession({
        identity: new DiscordIdentityProvider(sdkAdapter),
        instanceId: sdkAdapter.instanceId,
        colyseus: new RealColyseusClient(colyseusUrl),
        reconnectStorage: new SessionStorageReconnectStore(),
        classifyJoinError,
        classifyIdentityError,
      }),
      openExternalLink: (url) => { void sdkAdapter.openExternalLink(url); },
      speaking: sdkAdapter,
    });
  }
  if (createDevRealSessionFactory === null) {
    return Promise.reject(new Error('dev-real boot path is gone — should not happen in DEV builds'));
  }
  return createDevRealSessionFactory().then((m) => ({
    session: m.createDevRealSession(),
    openExternalLink: openViaWindow,
    speaking: null,
  }));
}

function RealApp({ mode: bootMode }: RealAppProps): JSX.Element {
  // StrictMode dev runs this lazy initializer TWICE (per React's impurity
  // check); only one of the two LobbySessions ends up stored in state, and
  // the other is discarded uninitialized. The double-mount of the effect
  // below is then made safe by `init()`'s inFlightInit cache + the
  // `cancelled` flag. See buildBootContext's comment for the constraint
  // this load-bears on (constructors must not open connections).
  const [bootPromise] = useState<Promise<BootContext>>(() => buildBootContext(bootMode));
  const [boot, setBoot] = useState<BootContext | null>(null);

  useEffect(() => {
    let cancelled = false;
    bootPromise.then((ctx) => {
      if (cancelled) return;
      setBoot(ctx);
      void ctx.session.init();
    }).catch((err: unknown) => {
      if (!cancelled) console.error('[lobby-session] boot promise rejected', err);
    });
    return () => { cancelled = true; };
  }, [bootPromise]);

  if (boot === null) {
    return <BootFallback bootPhase="identity" error={null} />;
  }

  return <RealAppInner session={boot.session} openExternalLink={boot.openExternalLink} speaking={boot.speaking} />;
}

interface RealAppInnerProps {
  session: LobbySession;
  openExternalLink: (url: string) => void;
  speaking: SpeakingEventSource | null;
}

function RealAppInner({ session, openExternalLink, speaking }: RealAppInnerProps): JSX.Element {
  const router = usePhaseRouter(session);
  const lobby = useLobbySession(session);
  const meeting = useMeetingSession(session);
  const cluegiving = useCluegivingSession(session);
  const discussion = useDiscussionSession(session);
  const reveal = useRevealSession(session);
  const gameEnd = useGameEndSession(session);
  const { bootPhase, error, connection } = router;

  /* Preferences live here and feed the single MetaControls mount below (plus
     useAudioDirector) — Settings is reachable from every phase through that
     fixed-position chrome, not per-phase Footer threading. The hook owns
     localStorage hydration + persistence, including the v0 `sound: boolean`
     → `{music,sounds}Volume` migration. */
  const [preferences, onPreferenceChange] = usePreferences();
  useAudioDirector(session, preferences);
  useReducedMotionAttribute(preferences.reducedMotion);

  /* Voice float wiring: Discord SPEAKING edges → SpeakingProvider around the
     phase node (the dev-mock equivalent lives in MockApp). The hook stays
     subscribed even while the gate suppresses the float (reveal arc, cinematic
     windows — the provider stays mounted but is handed an empty set) so the
     set is current the moment the float is allowed again. */
  const speakingIds = useDiscordSpeaking(speaking);
  const speakingAllowed =
    speaking !== null &&
    isSpeakingAllowed({
      phase: router.phase,
      transitioning: router.transitioning,
      cluegivingTransitioning: router.cluegivingTransitioning,
      gameEndTransitioning: router.gameEndTransitioning,
    });

  const lobbyHandlers: LobbyHandlers = useMemo(() => ({
    onFoolToggle: lobby.handlers.onFoolToggle,
    onTimerChange: lobby.handlers.onTimerChange,
    onStart: lobby.handlers.onStart,
  }), [lobby.handlers]);

  /* ── GameEnd → Lobby reset transition ────────────────────────────────────
     Three-stage state machine driving the warm-ink wash that covers the room
     while the host's Back-to-Lobby reset takes effect, then dissipates onto
     the fresh Lobby. See GameEndResetFade.tsx for the visual choreography.

       null      — no reset in flight; normal phase routing applies.
       covering  — host clicked Back-to-Lobby; wash is fading in. Server
                   action has NOT been dispatched yet — onPeak owns that
                   so the user sees a clean fade before the room churns.
       covered   — server has acknowledged (router.phase observed as Lobby);
                   wash starts its fade-out, revealing the fresh Lobby
                   underneath.

     Cold arrival is naturally bypassed: a player joining mid-reset never
     sets `resetting` (it's local state), so they jump straight to the
     settled fresh Lobby without ever seeing the wash. */
  const [resetting, setResetting] = useState<'covering' | 'covered' | null>(null);

  const handleBackToLobby = useCallback(() => {
    setResetting('covering');
  }, []);

  const handleResetPeak = useCallback(() => {
    /* Wash is fully opaque — dispatch the actual server action now. The
       phase change comes back via the router; we wait for it to flip the
       state to 'covered' below. Dep is the memoized callback, NOT the
       `gameEnd` result object — that is a fresh literal on every snapshot
       render, and a fresh `onPeak` identity mid-wash would restart the
       GSAP cover tween (flash-and-snap instead of the cover/dawn beat). */
    gameEnd.onBackToLobby();
  }, [gameEnd.onBackToLobby]);

  const handleResetComplete = useCallback(() => {
    setResetting(null);
  }, []);

  /* Phase-observation effect: once the server has applied the reset and we
     see the Lobby phase, advance the wash to its fade-out stage. */
  useEffect(() => {
    if (resetting === 'covering' && router.phase === GamePhase.Lobby) {
      setResetting('covered');
    }
  }, [resetting, router.phase]);

  /* Schema mismatch is a full-screen replacement: the ClosedDoorCard takes
     over the whole surface and asks the player to reload. Lives at the App
     shell so every phase gets the same treatment — disconnect UI is
     phase-agnostic. */
  if (connection === 'schemaMismatch') {
    return <ClosedDoorCard reason="schemaMismatch" />;
  }

  /* Decide the active phase node. Lobby + GhostMeeting (14a) and ClueGiving
     (14b) are wired; later phases land as they come online (until then
     refreshView maps them through the lobby mapper, so they fall to the
     BootFallback below). */
  let phaseNode: JSX.Element | null = null;
  if (router.phase === GamePhase.GhostMeeting && meeting.view !== null) {
    /* Unlike <Lobby> (which wraps itself in PageFrame),
       <GhostMeeting> returns bare spread content — MockApp's MeetingMock adds
       the frame chrome externally, so the App must too. Settings chrome is the
       App-level MetaControls mount below. */
    phaseNode = (
      <PageFrame>
        <GhostMeeting
          view={meeting.view}
          onVote={meeting.onVote}
          transitioning={router.transitioning}
          onTransitionComplete={router.completeTransition}
          justArrived={router.justArrived}
          {...(router.frozenLobbyView !== null ? { lobbyView: router.frozenLobbyView } : {})}
        />
      </PageFrame>
    );
  } else if (router.phase === GamePhase.ClueGiving && router.meetingSettling && router.frozenMeetingView !== null) {
    /* Meeting wind-down window: the server already moved to ClueGiving, but
       for MEETING_SETTLE_MS the router keeps the meeting on screen (off the
       frozen view) with `settling=true` so the V/F mystery grid eases to a
       rest instead of hard-cutting (MysteryFlipGrid mode='settling'). Same
       element shape as the GhostMeeting branch above, so React keeps the
       component instance mounted across the phase flip and any in-flight
       FLIP transition completes. The window self-clears in the router; the
       CluegivingPhase branch below then takes over with its entry beat. */
    phaseNode = (
      <PageFrame>
        <GhostMeeting
          view={router.frozenMeetingView}
          onVote={meeting.onVote}
          transitioning={router.transitioning}
          onTransitionComplete={router.completeTransition}
          settling
          justArrived={router.justArrived}
          {...(router.frozenLobbyView !== null ? { lobbyView: router.frozenLobbyView } : {})}
        />
      </PageFrame>
    );
  } else if (router.phase === GamePhase.ClueGiving && cluegiving.view !== null) {
    /* Same chrome as the meeting: <CluegivingPhase> returns bare spread
       content, so the App adds PageFrame; Settings chrome is the
       App-level MetaControls mount below. No page-turn — the entry beat
       is the lighter ink-in cascade owned by CluegivingPhase, driven by
       router.cluegivingTransitioning (handoff §1). Hence no frozenLobbyView
       here (it feeds the meeting's page-turn only). */
    phaseNode = (
      <PageFrame>
        <CluegivingPhase
          view={cluegiving.view}
          onSubmitClue={cluegiving.onSubmitClue}
          transitioning={router.cluegivingTransitioning}
          onTransitionComplete={router.completeCluegivingTransition}
          justArrived={router.justArrived}
        />
      </PageFrame>
    );
  } else if (router.phase === GamePhase.Discussion && discussion.view !== null) {
    /* Same bare-spread chrome as meeting/clue-giving; Settings chrome is the
       App-level MetaControls mount below. No page-turn; justArrived suppresses
       the cast/cluster/clue entry beats on a cold arrival. */
    phaseNode = (
      <PageFrame>
        <DiscussionPhase
          view={discussion.view}
          onVote={discussion.onVote}
          justArrived={router.justArrived}
        />
      </PageFrame>
    );
  } else if (router.phase === GamePhase.RoleReveal && reveal.roleReveal !== null) {
    phaseNode = (
      <PageFrame>
        <RoleReveal view={reveal.roleReveal} justArrived={router.justArrived} />
      </PageFrame>
    );
  } else if (router.phase === GamePhase.GhostGuessing && reveal.ghostGuessing !== null) {
    phaseNode = (
      <PageFrame>
        <GhostGuessing view={reveal.ghostGuessing} onSubmitGuess={reveal.onSubmitGuess} />
      </PageFrame>
    );
  } else if (router.phase === GamePhase.GuessReveal && reveal.guessReveal !== null) {
    phaseNode = (
      <PageFrame>
        <GuessReveal view={reveal.guessReveal} />
      </PageFrame>
    );
  } else if (router.phase === GamePhase.GameEnd && gameEnd.view !== null) {
    if (gameEnd.view.side === 'none') {
      /* Stalemate Lights-Out — full-bleed, no PageFrame (outside the book,
         like ClosedDoor). Matches MockApp's GameEndMock branch. */
      phaseNode = <GameEnd view={gameEnd.view} onBackToLobby={handleBackToLobby} />;
    } else {
      /* Village close + ghost burn — book outcomes wrap in PageFrame
         like the reveal arc; Settings chrome is the App-level MetaControls
         mount below. The cinematic plays under transitioning, and the
         frozenPriorPhase from the router supplies the underlay content. */
      phaseNode = (
        <PageFrame>
          <GameEnd
            view={gameEnd.view}
            onBackToLobby={handleBackToLobby}
            transitioning={router.gameEndTransitioning}
            onTransitionComplete={router.completeGameEndTransition}
            frozenPriorPhase={router.frozenPriorPhase}
          />
        </PageFrame>
      );
    }
  } else if (lobby.view !== null) {
    phaseNode = (
      <Lobby
        view={lobby.view}
        handlers={lobbyHandlers}
      />
    );
  }

  if (phaseNode === null) {
    return <BootFallback bootPhase={bootPhase} error={error} />;
  }

  /* The connection banner sits in the App-level `<main>` above whatever
     phase is currently mounted. The dim rule in Lobby.css uses
     `:has(.susdle-banner)` against this `<main>` to desaturate the spread
     beneath when connection is lost. Both phases share this wrapper so the
     banner mechanism is phase-agnostic. */
  return (
    <ClockOffsetContext.Provider value={router.clockOffset}>
      {/* 'always' also flips framer's useReducedMotion() consumers
          (AvatarFrame, RoleReveal, GuessReveal). */}
      <MotionConfig reducedMotion={preferences.reducedMotion ? 'always' : 'user'}>
        <main className="susdle-page-frame-host">
          {connection === 'connectionLost' ? (
            <div className="susdle-banner-row">
              <ConnectionBanner visible />
            </div>
          ) : null}
          {/* The wrap must be structurally stable: branching on `speakingAllowed`
              here would change the element type at this slot on every gate flip
              and remount the whole phase subtree — GhostMeeting/CluegivingPhase
              fire their reveal choreography off the transitioning true→false
              edge inside ONE instance, so a remount silently skips the
              cinematic. `speaking` never changes identity after boot, so that
              branch is safe; suppression instead empties the provider's set
              and airborne tiles glide home on the release spring. */}
          {speaking !== null ? (
            <SpeakingProvider speakingIds={speakingAllowed ? speakingIds : NO_SPEAKERS}>
              {phaseNode}
            </SpeakingProvider>
          ) : (
            phaseNode
          )}
          {/* Fixed-position chrome rendered once for every phase, including
              full-bleed screens like the stalemate GameEnd where PageFrame
              is absent — Settings would be unreachable there without this mount. */}
          <MetaControls
            preferences={preferences}
            onPreferenceChange={onPreferenceChange}
            openExternalLink={openExternalLink}
          />
        </main>
        {/* Reset wash — fixed full-bleed, mounted above <main> so it covers
            everything (phase node + banner). Lives entirely in App state;
            cold arrivals never see it. */}
        {resetting !== null ? (
          <GameEndResetFade
            fadeOut={resetting === 'covered'}
            onPeak={handleResetPeak}
            onComplete={handleResetComplete}
          />
        ) : null}
      </MotionConfig>
    </ClockOffsetContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// BootFallback — minimal fallback while the session is booting or has failed.
// ---------------------------------------------------------------------------

function BootFallback({
  bootPhase,
  error,
}: {
  bootPhase: BootPhase;
  error: { kind: ErrorKind; cause?: unknown } | null;
}): JSX.Element {
  // A failed boot replaces the whole screen with the closed-door surface.
  if (bootPhase === 'failed') {
    return <ClosedDoorCard reason={closedDoorReason(error?.kind)} />;
  }
  // Pre-ready states (identity / connecting / joining / reconnecting):
  // quiet wordmark + drawn-arc spinner on warm paper.
  return <BootLoader />;
}
